// @ts-nocheck

(() => {
    const SELECTOR = '[data-banner-2]';

    class Banner2 {
        constructor(root) {
            this.root = root;
            this.slides = Array.from(root.querySelectorAll('[data-banner-slide]'));
            this.dots = Array.from(root.querySelectorAll('[data-banner-dot]'));
            this.prevButton = root.querySelector('[data-banner-prev]');
            this.nextButton = root.querySelector('[data-banner-next]');
            this.playPauseButton = root.querySelector('[data-banner-play-pause]');
            this.playIcon = root.querySelector('[data-banner-play-icon]');
            this.pauseIcon = root.querySelector('[data-banner-pause-icon]');
            this.status = root.querySelector('[data-banner-status]');
            this.viewport = root.querySelector('[data-banner-viewport]');

            this.currentIndex = Math.max(
                0,
                this.slides.findIndex((slide) => slide.classList.contains('is-active'))
            );

            this.autoplayEnabled = root.dataset.autoplay === 'true';
            this.pauseOnHover = root.dataset.pauseOnHover === 'true';
            this.loop = root.dataset.loop === 'true';
            this.delay = Number(root.dataset.autoplayDelay) || 6000;
            this.timer = null;
            this.isPausedByUser = false;
            this.isHovered = false;
            this.touchStartX = 0;
            this.touchEndX = 0;

            this.onVisibilityChange = this.onVisibilityChange.bind(this);
            this.onKeydown = this.onKeydown.bind(this);
            this.updateViewportHeight = this.updateViewportHeight.bind(this);

            this.bindEvents();
            this.activateSlide(this.currentIndex, false);
            this.updateViewportHeight();
            this.startAutoplay();
        }

        bindEvents() {
            this.prevButton?.addEventListener('click', () => {
                this.previous();
                this.restartAutoplay();
            });

            this.nextButton?.addEventListener('click', () => {
                this.next();
                this.restartAutoplay();
            });

            this.dots.forEach((dot) => {
                dot.addEventListener('click', () => {
                    this.goTo(Number(dot.dataset.bannerDot));
                    this.restartAutoplay();
                });
            });

            this.playPauseButton?.addEventListener('click', () => {
                this.isPausedByUser = !this.isPausedByUser;
                this.updatePlayPauseButton();

                if (this.isPausedByUser) {
                    this.stopAutoplay();
                } else {
                    this.startAutoplay();
                }
            });

            if (this.pauseOnHover) {
                this.root.addEventListener('mouseenter', () => {
                    this.isHovered = true;
                    this.stopAutoplay();
                });

                this.root.addEventListener('mouseleave', () => {
                    this.isHovered = false;
                    this.startAutoplay();
                });
            }

            this.root.addEventListener('keydown', this.onKeydown);
            window.addEventListener('resize', this.updateViewportHeight);

            this.root.querySelectorAll('img, video').forEach((media) => {
                media.addEventListener('load', this.updateViewportHeight);
                media.addEventListener('loadedmetadata', this.updateViewportHeight);
            });

            this.root.addEventListener(
                'touchstart',
                (event) => {
                    this.touchStartX = event.changedTouches[0].screenX;
                },
                { passive: true }
            );

            this.root.addEventListener(
                'touchend',
                (event) => {
                    this.touchEndX = event.changedTouches[0].screenX;
                    this.handleSwipe();
                },
                { passive: true }
            );

            document.addEventListener('visibilitychange', this.onVisibilityChange);
        }

        onKeydown(event) {
            if (event.key === 'ArrowLeft') {
                this.previous();
                this.restartAutoplay();
            }

            if (event.key === 'ArrowRight') {
                this.next();
                this.restartAutoplay();
            }
        }

        onVisibilityChange() {
            if (document.hidden) {
                this.stopAutoplay();
            } else {
                this.startAutoplay();
            }
        }

        handleSwipe() {
            const distance = this.touchStartX - this.touchEndX;

            if (Math.abs(distance) < 50) return;

            if (distance > 0) {
                this.next();
            } else {
                this.previous();
            }

            this.restartAutoplay();
        }

        goTo(index) {
            if (!this.slides.length || index === this.currentIndex) return;

            const boundedIndex = Math.max(0, Math.min(index, this.slides.length - 1));
            this.activateSlide(boundedIndex, true);
        }

        next() {
            if (!this.slides.length) return;

            if (this.currentIndex === this.slides.length - 1) {
                if (this.loop) this.goTo(0);
                else this.stopAutoplay();
                return;
            }

            this.goTo(this.currentIndex + 1);
        }

        previous() {
            if (!this.slides.length) return;

            if (this.currentIndex === 0) {
                if (this.loop) this.goTo(this.slides.length - 1);
                return;
            }

            this.goTo(this.currentIndex - 1);
        }

        activateSlide(index, announce = true) {
            this.slides.forEach((slide, slideIndex) => {
                const isActive = slideIndex === index;
                slide.classList.toggle('is-active', isActive);
                slide.setAttribute('aria-hidden', String(!isActive));

                if (isActive) {
                    this.activateMedia(slide);
                } else {
                    this.deactivateMedia(slide);
                }
            });

            this.dots.forEach((dot, dotIndex) => {
                const isActive = dotIndex === index;
                dot.classList.toggle('is-active', isActive);
                dot.setAttribute('aria-current', String(isActive));
            });

            this.currentIndex = index;
            this.updateViewportHeight();

            if (announce && this.status) {
                this.status.textContent = `Slide ${index + 1} of ${this.slides.length}`;
            }
        }

        activateMedia(slide) {
            this.getRelevantMedia(slide).forEach((media) => {
                if (media.tagName === 'IFRAME') {
                    if (!media.src && media.dataset.src) {
                        media.src = media.dataset.src;
                    }
                    return;
                }

                if (media.tagName === 'VIDEO' && media.autoplay) {
                    const playPromise = media.play();
                    if (playPromise?.catch) playPromise.catch(() => { });
                }
            });
        }

        deactivateMedia(slide) {
            slide.querySelectorAll('video').forEach((video) => {
                video.pause();
            });

            slide.querySelectorAll('[data-banner-external-video]').forEach((iframe) => {
                if (iframe.src) {
                    iframe.removeAttribute('src');
                }
            });
        }

        getRelevantMedia(slide) {
            const isMobile = window.matchMedia('(max-width: 749px)').matches;
            const scope = isMobile
                ? slide.querySelector('.banner-2__video-wrap--mobile')
                : slide.querySelector('.banner-2__video-wrap--desktop');

            return scope
                ? Array.from(scope.querySelectorAll('video, [data-banner-external-video]'))
                : [];
        }

        updateViewportHeight() {
            if (!this.viewport || window.matchMedia('(max-width: 749px)').matches) return;

            const activeSlide = this.slides[this.currentIndex];
            const media = activeSlide?.querySelector('.banner-2__image, .banner-2__video');
            const intrinsicWidth = media?.naturalWidth || media?.videoWidth;
            const intrinsicHeight = media?.naturalHeight || media?.videoHeight;

            if (!intrinsicWidth || !intrinsicHeight) return;

            const height = Math.min(
                750,
                Math.round((this.viewport.clientWidth * intrinsicHeight) / intrinsicWidth)
            );

            this.root.style.setProperty('--banner-2-current-height', `${height}px`);
        }

        startAutoplay() {
            if (
                !this.autoplayEnabled ||
                this.isPausedByUser ||
                this.isHovered ||
                document.hidden ||
                this.slides.length < 2
            ) {
                return;
            }

            this.stopAutoplay();
            this.timer = window.setInterval(() => this.next(), this.delay);
        }

        stopAutoplay() {
            if (!this.timer) return;
            window.clearInterval(this.timer);
            this.timer = null;
        }

        restartAutoplay() {
            this.stopAutoplay();
            this.startAutoplay();
        }

        updatePlayPauseButton() {
            if (!this.playPauseButton) return;

            this.playPauseButton.setAttribute(
                'aria-label',
                this.isPausedByUser ? 'Play slideshow' : 'Pause slideshow'
            );
            this.playPauseButton.setAttribute(
                'aria-pressed',
                String(this.isPausedByUser)
            );

            if (this.playIcon) this.playIcon.hidden = !this.isPausedByUser;
            if (this.pauseIcon) this.pauseIcon.hidden = this.isPausedByUser;
        }

        destroy() {
            this.stopAutoplay();
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
            this.root.removeEventListener('keydown', this.onKeydown);
            window.removeEventListener('resize', this.updateViewportHeight);
            delete this.root.banner2Instance;
        }
    }

    const initialize = (scope = document) => {
        scope.querySelectorAll(SELECTOR).forEach((root) => {
            if (root.banner2Instance) return;
            root.banner2Instance = new Banner2(root);
        });
    };

    initialize();

    document.addEventListener('shopify:section:load', (event) => {
        initialize(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
        event.target.querySelectorAll(SELECTOR).forEach((root) => {
            root.banner2Instance?.destroy();
        });
    });

    document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-banner-slide]');
        const root = event.target.closest(SELECTOR);

        if (!slide || !root?.banner2Instance) return;

        root.banner2Instance.stopAutoplay();
        root.banner2Instance.activateSlide(Number(slide.dataset.slideIndex), false);
    });

    document.addEventListener('shopify:block:deselect', (event) => {
        const root = event.target.closest(SELECTOR);
        root?.banner2Instance?.startAutoplay();
    });
})();
